/**
 * Badge / Chip — a compact status / category label. The colour is driven by a
 * `tone`: either a semantic accent ("rose" | "gold" | "success" | "danger" |
 * "neutral") or a directive `kind` (e.g. "character", "dialogue", "choice")
 * that resolves to a `--kind-*` token. Variants: tonal (soft wash, default),
 * solid (strong fill), outline (hairline). Optional leading dot.
 *
 * No hardcoded colours: the resolved tone is passed as the `--lc-tone` custom
 * property and the CSS mixes it against the surface.
 */
import { forwardRef, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";

export type BadgeVariant = "tonal" | "solid" | "outline";
export type BadgeSize = "sm" | "md" | "pill";
export type BadgeTone = "rose" | "gold" | "success" | "danger" | "neutral";

/** The directive kinds that map to a --kind-* token. */
export type BadgeKind =
  | "character"
  | "dialogue"
  | "narration"
  | "bg"
  | "music"
  | "sfx"
  | "cg"
  | "choice"
  | "effect"
  | "phone"
  | "if"
  | "minigame"
  | "gate"
  | "ending"
  | "record";

const TONE_VAR: Record<BadgeTone, string> = {
  rose: "var(--accent-rose)",
  gold: "var(--accent-gold)",
  success: "var(--accent-success)",
  danger: "var(--accent-danger)",
  neutral: "var(--text-secondary)",
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** Fill style. @default "tonal" */
  readonly variant?: BadgeVariant;
  /** Size / shape. @default "md" */
  readonly size?: BadgeSize;
  /** Semantic colour. Ignored when `kind` is set. @default "neutral" */
  readonly tone?: BadgeTone;
  /** Directive kind — resolves to a --kind-* colour, overriding `tone`. */
  readonly kind?: BadgeKind;
  /** Show a small leading status dot. */
  readonly dot?: boolean;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = "tonal", size = "md", tone = "neutral", kind, dot, className, style, children, ...rest },
  ref,
) {
  const toneValue = kind ? `var(--kind-${kind})` : TONE_VAR[tone];
  const classes = [
    "lc-badge",
    `lc-badge--${variant}`,
    `lc-badge--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const mergedStyle = { ...style, ["--lc-tone" as string]: toneValue } as CSSProperties;
  return (
    <span ref={ref} className={classes} style={mergedStyle} {...rest}>
      {dot ? <span className="lc-badge__dot" aria-hidden="true" /> : null}
      {children as ReactNode}
    </span>
  );
});
